<template>
  <div class="marketplace-reset-password">
    <h2>{{ 'New password' | t }}</h2>
    <p>{{ 'Please enter your new password.' | t }}</p>
    <form action="#" v-stack-y:sm @submit.prevent="handleSubmit">
      <SloField>
        <SloLabel>{{ 'Password' | t }}</SloLabel>
        <SloInput
          ref="input"
          name="password"
          v-model="form.password"
          type="password"
          @blur="displayConfirmPasswordErrorCheck"
          required
        />
      </SloField>
      <SloPasswordValidation
        :password="form.password"
        :confirm-password="form.confirmPassword"
        @status="handlePasswordValidationStatus"
      />
      <SloField>
        <SloLabel :error="displayConfirmPasswordError">{{ 'Confirm Password' | t }}</SloLabel>
        <SloInput
          v-model="form.confirmPassword"
          :error="displayConfirmPasswordError && 'Passwords must match' | t"
          @blur="displayConfirmPasswordErrorCheck"
          type="password"
          name="confirmPassword"
          required
        />
      </SloField>
      <div class="button-wrapper mt-lg">
        <SloButton :to="{name: 'marketplace-auth-login'}" fill="outline">
          {{ 'Cancel' | t }}
        </SloButton>
        <SloButton type="submit" :disabled="isSubmitDisabled">
          <SloLoader :loading="loading" color="white" size="md">
            {{ 'Reset Password' | t }}
          </SloLoader>
        </SloButton>
      </div>
    </form>
  </div>
</template>

<script>
import t from '../../../js/main/i18n';
import {authApi} from '../apis/auth';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloPasswordValidation from '../../../lib/components/SloPasswordValidation.vue';

export default {
  components: {
    SloField,
    SloLabel,
    SloInput,
    SloButton,
    SloLoader,
    SloPasswordValidation
  },
  filters: {
    t
  },
  async mounted() {
    this.$nextTick(() => this.$refs.input.setFocus());
    if (!this.$route.query?.token) {
      await this.$router.push({name: 'marketplace-auth-login'});
      this.$snotify.error(t('No reset token provided'));
    }
    this.token = this.$route.query.token;
  },
  data() {
    return {
      token: null,
      loading: false,
      error: null,
      passwordValidated: false,
      confirmPasswordValidated: false,
      displayConfirmPasswordError: false,
      form: {
        password: '',
        confirmPassword: ''
      }
    };
  },
  watch: {
    token: function() {
      this.error = null;
    },
    'form.password'() {
      this.error = null;
    },
    'form.confirmPassword'() {
      this.error = null;
    }
  },
  computed: {
    isSubmitDisabled() {
      return this.loading
        || !this.passwordValidated
        || !this.confirmPasswordValidated;
    }
  },
  methods: {
    handlePasswordValidationStatus(status) {
      this.passwordValidated = status.password;
      this.confirmPasswordValidated = status.confirmPassword;
    },
    displayConfirmPasswordErrorCheck() {
      if (this.form.confirmPassword) {
        this.displayConfirmPasswordError = !this.confirmPasswordValidated;
      }
    },
    async handleSubmit() {
      this.loading = true;

      await authApi.resetPassword({
        password: this.form.password,
        token: this.token
      })
        .then(async() => {
          this.loading = false;
          this.$snotify.success(t('Password successfully reset, please sign in'));
          await this.$router.replace({name: 'marketplace-auth-login'});
        }).catch(() => {
          this.loading = false;
          this.$snotify.error(t('Reset token is invalid or expired'));
        });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-reset-password {
  margin-block: 6.4rem;

  > h2, > p {
    text-align: center;
  }

  form {
    width: 100%;
    max-width: 410px;
    margin-block-start: 3.2rem;
    margin-inline: auto;
    padding: 1.6rem;
    border: 1px solid #d1d5db;
    border-radius: 1.2rem;

    @media (min-width: $screen-sm) {
      padding: 3.2rem;
    }

    .slo-field:not(:last-of-type) {
      margin-top: 0 !important;
    }
  }

  .button-wrapper {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    gap: 1.6rem;

    @media (min-width: $screen-sm) {
      gap: 3.2rem;
    }

    > * {
      flex: 1 1 50%;
      width: 100%;
    }

    .slo-button {
      width: 100%;
    }
  }
}

.error-confirm-password {
  @include text('sm', 'strong');
  color: $color-red-400;
}
</style>
